import type { ProductDataType } from '@am3nd-org/pointcraft-types';

import { ApiMethods, apiSenderV2, apiEndpoints } from 'src/utils/axios';

import { useDeviceSyncStore } from 'src/store/DeviceSync';
import {
  getSessionGeneration,
  registerSessionCleanup,
  isSessionGenerationCurrent,
} from 'src/auth/session-lifecycle';

import { commitInventoryWrite } from './inventory-write-barrier';
import {
  InventoryLocalDB,
  getInventorySyncMeta,
  saveInventorySyncMeta,
  INVENTORY_CACHE_SCHEMA_VERSION,
} from './DB_Utils';

// Bootstrap, review refreshes and Firestore updates share one ordered writer.
// New updates wait their turn instead of being dropped during a pending fetch.
let work: Promise<unknown> = Promise.resolve();
registerSessionCleanup(() => {
  // Old network requests may still finish; their admission checks reject them.
  // A new tenant need not wait for that transport timeout to begin its own read.
  work = Promise.resolve();
});
export interface InventoryLoadOptions {
  force?: boolean;
  version?: number;
  updatedAt?: Date;
  earliestChangedAt?: Date;
  forceFull?: boolean;
  fullReconcileVersion?: number;
}

export function loadInventory(
  businessId: string,
  options: InventoryLoadOptions = {}
): Promise<void> {
  const generation = getSessionGeneration();
  const current = () =>
    isSessionGenerationCurrent(generation) &&
    useDeviceSyncStore.getState().businessData?.id === businessId;
  const assertCurrent = () => {
    if (!current()) throw new Error('Inventory session changed.');
  };
  const commit = (callback: () => Promise<unknown>) =>
    commitInventoryWrite(async () => {
      assertCurrent();
      await callback();
      assertCurrent();
    });

  const pending = work.then(async () => {
    assertCurrent();
    await commit(() => InventoryLocalDB.migrateFromChunks());
    const [meta, count] = await Promise.all([getInventorySyncMeta(), InventoryLocalDB.count()]);
    assertCurrent();
    const ownsCache =
      meta?.businessId === businessId &&
      meta.schemaVersion === INVENTORY_CACHE_SCHEMA_VERSION &&
      meta.recordCount === count;
    const changeFloor = options.earliestChangedAt?.getTime();
    const hasDurableCursor =
      Number.isFinite(changeFloor) &&
      Number.isSafeInteger(options.fullReconcileVersion) &&
      options.fullReconcileVersion! >= 0;
    const unsafeDelta =
      ownsCache && Number.isFinite(changeFloor) && changeFloor! <= Date.parse(meta.lastSyncedAt);
    const missedReconciliation =
      ownsCache &&
      options.fullReconcileVersion !== undefined &&
      options.fullReconcileVersion > meta.version;
    const forceFull =
      options.force ||
      options.forceFull ||
      unsafeDelta ||
      missedReconciliation ||
      (options.version !== undefined && (!hasDurableCursor || !ownsCache || meta.version <= 0));
    if (
      ownsCache &&
      !options.force &&
      (options.version === undefined ? !forceFull : meta.version >= options.version)
    )
      return;

    // The request start watermark includes changes during an initial fetch in
    // the following delta, including an initially empty menu.
    const startedAt = new Date();
    const incremental = ownsCache && !forceFull && Boolean(meta.lastSyncedAt);
    const url = incremental
      ? `${apiEndpoints.products.changes}?since=${encodeURIComponent(meta.lastSyncedAt)}`
      : apiEndpoints.products.base;
    const response = await apiSenderV2(url, ApiMethods.GET, null, true, null, {
      throwOnError: true,
      includeOperatorSession: false,
    });
    assertCurrent();
    if (
      incremental
        ? !Array.isArray(response?.updated) || !Array.isArray(response?.deleted)
        : !Array.isArray(response)
    )
      throw new Error('Unable to load the restaurant menu.');

    await commit(async () => {
      // A failed or interrupted multi-step write must not appear complete.
      await saveInventorySyncMeta({
        version: -1,
        lastSyncedAt: '',
        businessId,
        schemaVersion: INVENTORY_CACHE_SCHEMA_VERSION,
        recordCount: -1,
      });
      assertCurrent();
      if (incremental) {
        await InventoryLocalDB.upsertMany(response.updated as ProductDataType[]);
        assertCurrent();
        await Promise.all(
          (response.deleted as string[]).map((id) => InventoryLocalDB.hardDelete(id))
        );
      } else {
        await InventoryLocalDB.clearAll();
        assertCurrent();
        await InventoryLocalDB.saveAll(response as ProductDataType[]);
      }
      assertCurrent();
      const recordCount = await InventoryLocalDB.count();
      assertCurrent();
      // Stay before the committed batch's change floor, never its publication
      // time. A later notice retains a reconciliation marker if floors regress.
      const watermark = Number.isFinite(changeFloor)
        ? new Date(Math.min(changeFloor! - 1, startedAt.getTime()))
        : startedAt;
      await saveInventorySyncMeta({
        version: options.version ?? 0,
        lastSyncedAt: watermark.toISOString(),
        businessId,
        schemaVersion: INVENTORY_CACHE_SCHEMA_VERSION,
        recordCount,
      });
    });
  });
  work = pending.catch(() => undefined);
  return pending;
}
